import { useQueryClient } from '@tanstack/react-query';
import { Check, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Field, Select, useToast } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { usePatientActions } from '@/features/patients/api';
import {
  BLOOD_TYPES,
  type BloodTypeValue,
  type PatientDetail,
} from '@/features/patients/types';
import { isApiError } from '@/lib/api';
import { appAccountTime } from './app-account-format';
import styles from './PatientBloodType.module.css';

const label = (v: BloodTypeValue) => (v === 'unknown' ? 'Unknown' : v);

/**
 * RL-1157: the blood type (RL-951 `bloodType`) with where it came from:
 * "Patient-reported" (set in the app, not confirmed) or "Confirmed by staff".
 * With patients:update on a live record, staff confirm the reported value or
 * correct / record it via PATCH /patients/:id { version, bloodType } (saved
 * as staff_confirmed).
 */
export function PatientBloodType({
  patient,
}: {
  patient: Pick<PatientDetail, 'id' | 'status' | 'version' | 'bloodType'>;
}) {
  const { hasPermission } = useSession();
  const [editing, setEditing] = useState(false);
  const { setBloodType } = usePatientActions();
  const save = useSave(patient);
  const b = patient.bloodType ?? null;
  const canEdit =
    hasPermission('patients:update') && patient.status !== 'merged';
  const reported = b?.source === 'patient_reported';

  return (
    <div className={styles.wrap} role="group" aria-label="Blood type">
      {b ? (
        <span className={styles.line}>
          <span className={styles.value}>{label(b.value)}</span>
          <Badge tone={reported ? 'warning' : 'success'} dot>
            {reported ? 'Patient-reported' : 'Confirmed by staff'}
          </Badge>
          {b.updatedAt && (
            <span className={styles.meta}>{appAccountTime(b.updatedAt)}</span>
          )}
        </span>
      ) : (
        <span className={styles.meta}>Not recorded</span>
      )}
      {reported && (
        <span className={styles.meta}>
          Set by the patient in the app; not confirmed yet.
        </span>
      )}
      {canEdit && !editing && (
        <span className={styles.actions}>
          {b && reported && (
            <Button
              size="sm"
              icon={<Check size={14} />}
              loading={setBloodType.isPending}
              aria-label={`Confirm blood type ${label(b.value)}`}
              onClick={() => void save.run(b.value)}
            >
              Confirm {label(b.value)}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            icon={<Pencil size={14} />}
            onClick={() => {
              save.reset();
              setEditing(true);
            }}
          >
            {b ? 'Correct' : 'Record blood type'}
          </Button>
        </span>
      )}
      {editing && (
        <CorrectForm
          initial={b?.value ?? ''}
          save={save}
          onDone={() => setEditing(false)}
        />
      )}
      {!editing && save.failure && <FailureNote failure={save.failure} />}
    </div>
  );
}

interface Failure {
  message: string;
  requestId?: string;
}

function useSave(patient: Pick<PatientDetail, 'id' | 'version' | 'bloodType'>) {
  const qc = useQueryClient();
  const toast = useToast();
  const { setBloodType } = usePatientActions();
  const [failure, setFailure] = useState<Failure | null>(null);
  const [fieldError, setFieldError] = useState<string>();

  async function run(value: BloodTypeValue): Promise<boolean> {
    setFailure(null);
    setFieldError(undefined);
    try {
      await setBloodType.mutateAsync({
        id: patient.id,
        version: patient.version,
        bloodType: value,
      });
      toast.show({
        tone: 'success',
        title: `Blood type ${label(value)} confirmed`,
      });
      return true;
    } catch (err) {
      if (!isApiError(err)) {
        setFailure({ message: 'Could not save the blood type.' });
        return false;
      }
      setFieldError(err.fieldError('bloodType'));
      setFailure({
        message:
          err.code === 'VERSION_CONFLICT'
            ? 'Someone else changed this record. It has been reloaded: check the blood type and try again.'
            : err.code === 'PATIENT_MERGED'
              ? 'This record was merged into another. Use the kept record.'
              : err.message,
        requestId: err.requestId,
      });
      if (err.code === 'VERSION_CONFLICT' || err.code === 'PATIENT_MERGED') {
        void qc.invalidateQueries({ queryKey: ['patient', patient.id] });
        void qc.invalidateQueries({ queryKey: ['patient-record', patient.id] });
      }
      return false;
    }
  }

  return {
    run,
    failure,
    fieldError,
    pending: setBloodType.isPending,
    reset: () => {
      setFailure(null);
      setFieldError(undefined);
    },
  };
}

function CorrectForm({
  initial,
  save,
  onDone,
}: {
  initial: BloodTypeValue | '';
  save: ReturnType<typeof useSave>;
  onDone: () => void;
}) {
  const [value, setValue] = useState<BloodTypeValue | ''>(initial);
  const submit = async () => {
    if (value && (await save.run(value))) onDone();
  };
  return (
    <div
      className={styles.form}
      role="group"
      aria-label="Correct blood type"
      onKeyDown={(e) => {
        // The patient drawer is a form: Enter here saves the blood type only.
        if (e.key !== 'Enter' || e.target instanceof HTMLButtonElement) return;
        e.preventDefault();
        if (value && !save.pending) void submit();
      }}
    >
      <Field
        label="Blood type"
        hint="Saved as confirmed by staff."
        error={save.fieldError}
      >
        <Select
          value={value}
          placeholder="Choose"
          onChange={(e) => setValue(e.target.value as BloodTypeValue | '')}
          options={BLOOD_TYPES.map((v) => ({ value: v, label: label(v) }))}
        />
      </Field>
      {save.failure && <FailureNote failure={save.failure} />}
      <span className={styles.actions}>
        <Button
          size="sm"
          variant="primary"
          disabled={!value}
          loading={save.pending}
          onClick={() => void submit()}
        >
          Save blood type
        </Button>
        <Button
          size="sm"
          onClick={() => {
            save.reset();
            onDone();
          }}
        >
          Cancel
        </Button>
      </span>
    </div>
  );
}

function FailureNote({ failure }: { failure: Failure }) {
  const { t } = useTranslation();
  return (
    <p className={styles.error} role="alert">
      {failure.message}
      {failure.requestId && (
        <span>{t('common.requestId', { id: failure.requestId })}</span>
      )}
    </p>
  );
}
